import { useCallback } from "react";
import type { MessageKey } from "@wato/i18n";
import { useI18n } from "@wato/i18n/react";
import { isApiError } from "../../api/client";
import { useErrorMessage } from "../../api/context";

const CONTACT_ERROR_CODES = new Set([
  "CONTACT_PHONE_INVALID",
  "CONTACT_VALIDATION_FAILED",
  "CONTACT_FIELD_INVALID",
  "CONTACT_FIELD_UNKNOWN",
  "CONTACT_NOT_FOUND",
  "LABEL_NOT_FOUND",
  "CUSTOM_FIELD_NOT_FOUND",
  "CONTACT_PHONE_EXISTS",
  "LABEL_NAME_TAKEN",
  "CUSTOM_FIELD_KEY_TAKEN",
]);

/**
 * Contacts API error → catalog text for its code (UI language), else the generic message.
 * Stable identity: the contacts list puts it in effect deps, so a new function per render
 * refetched the labels forever (160 requests in 35s, live 2026-10-31).
 */
export function useContactsError(): (error: unknown) => string {
  const { t } = useI18n();
  const generic = useErrorMessage();
  return useCallback(
    (error) => {
      if (isApiError(error) && CONTACT_ERROR_CODES.has(error.code)) {
        return t(`contacts.errors.${error.code}` as MessageKey);
      }
      return generic(error);
    },
    [t, generic],
  );
}

/** Phone numbers are always left-to-right, even inside Arabic text. */
export const PHONE_DIR = "ltr" as const;

/**
 * Q4-CNT-01: «add contact» shows its own field error, so the global write-failure toast
 * (which maps 409 to "changed elsewhere") must stay quiet for it.
 */
export function isInlineContactCreateError(
  error: { readonly code: string },
  request: { readonly method: string; readonly path: string },
): boolean {
  return (
    request.method === "POST" &&
    /^\/v1\/workspaces\/[^/]+\/contacts$/.test(request.path.split("?")[0] ?? "") &&
    CONTACT_ERROR_CODES.has(error.code)
  );
}

/** Merge failures are shown inside the merge dialog, so the global write-failure toast stays quiet. */
export function isInlineContactMergeError(request: {
  readonly method: string;
  readonly path: string;
}): boolean {
  return (
    request.method === "POST" &&
    /^\/v1\/workspaces\/[^/]+\/contacts\/[^/]+\/merge$/.test(request.path.split("?")[0] ?? "")
  );
}

/** Merge API error (400 / 404 / 409 / other) → AR/EN dialog message. */
export function useMergeError(): (error: unknown) => string {
  const { t } = useI18n();
  return useCallback(
    (error) => {
      if (isApiError(error)) {
        if (error.code === "CONTACT_NOT_FOUND" || error.status === 404) {
          return t("contacts.merge.errors.CONTACT_NOT_FOUND");
        }
        if (error.code === "CONTACT_VALIDATION_FAILED" || error.status === 400) {
          return t("contacts.merge.errors.CONTACT_VALIDATION_FAILED");
        }
        if (error.status === 409) return t("contacts.merge.errors.conflict");
      }
      return t("contacts.merge.errors.generic");
    },
    [t],
  );
}
